import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { ReasonMappingGrid } from '../grids';
// Imported by path, not the `../dialogs` barrel: that barrel also re-exports AddPayerDialog, whose
// chain pulls in ContractService and the app entry point, which a jest run of this tab cannot load.
import { AddEditReasonMappingDialog } from '../dialogs/AddEditReasonMappingDialog';
import {
  useGetCenterQuery,
  useUpdateCenterMutation,
} from '../../../../services/ConfigurationService';
import { ReasonMappingType } from '../../../../services/ConfigurationService/types/centerType';

export const ReasonMappingTab: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const company = queryParams.get("company") || "";

  const { data: center, refetch } = useGetCenterQuery({ id: id || "", company }, { skip: !id || !company });
  const [updateCenter] = useUpdateCenterMutation();

  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<ReasonMappingType | null>(null);
  const [saveError, setSaveError] = useState('');

  const reasonMappings = center?.reasonMappings || [];

  const handleAddMapping = () => {
    setEditingMapping(null);
    setMappingDialogOpen(true);
  };

  const handleRowSelect = (mapping: ReasonMappingType) => {
    setEditingMapping(mapping);
    setMappingDialogOpen(true);
  };

  // The dialog hands back the whole list to save, and has closed by now, so a refused save needs its
  // own message. RTK Query resolves a refusal with `{ error }` rather than rejecting.
  const handleSaveMappings = async (savedMappings: ReasonMappingType[]) => {
    if (!center) return;
    setSaveError('');
    const result = await updateCenter({ ...center, reasonMappings: savedMappings });
    if ('error' in result) {
      setSaveError('The reason mappings could not be saved. Try again.');
    }
    refetch();
  };

  const handleCloseMappingDialog = () => {
    setMappingDialogOpen(false);
    setEditingMapping(null);
  };

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Adjustment Reasons
          </Typography>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddMapping}>
            Add mapping
          </Button>
        </Stack>
        {saveError && <Alert severity="error">{saveError}</Alert>}
        <ReasonMappingGrid reasonMappings={reasonMappings} onRowSelect={handleRowSelect} />
      </Stack>

      <AddEditReasonMappingDialog
        open={mappingDialogOpen}
        onClose={handleCloseMappingDialog}
        reasonMappings={reasonMappings}
        onSave={handleSaveMappings}
        onDelete={handleSaveMappings}
        editingMapping={editingMapping}
      />
    </Stack>
  );
};
